import React, { useEffect } from "react";
import { useState } from "react";
import {
  faBolt,
  faCalendarDays,
  faImage,
  faLeaf,
  faMap,
  faTengeSign,
  faTextSlash,
  faTextWidth,
} from "@fortawesome/free-solid-svg-icons";
import "./styles/event-form.css";
import { Link, useNavigate } from "react-router-dom";
import FormSectionContainer from "./FormSectionContainer";
import { SearchIcon } from "../../utils/CustomIcon";
import { convertToISOString, formatToDateString, formatToTimeString } from "./utils/utils";
import ImageAlert from "./ImageAlert";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ImageUploader from "./ImageUploader";
import Description from "./Description";
import { EventFormData } from "./types/eventForm";
import Swal from "sweetalert2";
import { eventsAPI } from "../utils/API";
import { LoaderSm } from "../../common/components/Loader/Loader";
import { pageNames } from "../../config/pageNames";
import Select from "react-select";
import useAuthUserId from "auth/hooks/useAuthUser";
import eventTags from '../data/eventTags.json';
import TimeZoneSelect from "./TimeZoneSelect";
import { EventTag } from "./types/EventTag";
import { UserSDG } from "sdgselection/types/SDG";
import userSdgAPI from "sdgselection/utils/API";
import UserSdgSelect from "./UserSdgSelect";
import { SubscriptionGate } from "common/components/subscription/SubscriptionGate";

const tagOptions: EventTag[] = eventTags;

interface IProps {
  event?: EventFormData;
  id?: string;
  userEmail: string;
}

const EventForm: React.FC<IProps> = ({id, event, userEmail}) => {
  const evenTitleLimit = 75;
  const evenSummaryLimit = 140;

  const navigate = useNavigate();
  // form state

  // demo data
  const userId = useAuthUserId();
  const attendee_estimate = 200;
  const supplier_estimate = 20;
  // demo data

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [formIsPopulated, setFormIsPopulated] = useState<boolean>(false);
  // basic section
  const [eventTitle, setEventTitle] = useState("");
  const [eventOrganizer, setEventOrganizer] = useState("Event Organizer Inc.");
  const [eventType, setEventType] = useState("");
  const [eventCategory, setEventCategory] = useState("");
  const [eventSearchTag, setEventSearchTag] = useState<string[]>([]);
  const [selectedTags, setSelectedTags] = useState<EventTag[]>([]);
  const [estimatedAttendees, setEstimatedAttendees] = useState<number>(0);

  // location section
  const [searchVenueQuery, setSearchVenueQuery] = useState("");
  const [activeEventLocation, setActiveEventLocation] = useState("Physical");
  const [isMultiVenue, setIsMultiVenue] = useState<boolean>(false);

  // date and time
  const [isRecurring, setIsRecurring] = useState<boolean | null>(false);
  const [eventStartDate, setEventStartDate] = useState(formatToDateString(new Date()));
  const [eventStartTime, setEventStartTime] = useState(formatToTimeString(new Date()));

  const [eventEndDate, setEventEndDate] = useState<any>(formatToDateString(new Date()));
  const [eventEndTime, setEventEndTime] = useState<any>(formatToTimeString(new Date()));

  const [showStartTime, setShowStartTime] = useState(true);
  const [showEndTime, setShowEndTime] = useState(true);

  const [eventTimeZone, setEventTimeZone] = useState("UTC");
  const [eventPageLanguage, setEventPageLanguage] = useState("English");
  const [eventStatus, setEventStatus] = useState("Published");

  // event media
  const [displayMediaAlert, setDisplayMediaAlert] = useState(true);
  const [image, setImage] = useState<string | null>(null);

  // summary
  const [eventSummary, setEventSummary] = useState("");

  // description
  const [content, setContent] = useState<string>("");

  // sdg priority
  const [userSdgList, setUserSdgList] = useState<UserSDG[]>([]);
  const [selectedUserSdg, setSelectedUserSdg] = useState<UserSDG>();
  const [userSdgId, setUserSdgId] = useState<string | null>("");

  // Effect to pre-populate form fields if event prop is provided
  useEffect(() => {
    if (event && !formIsPopulated) {
      setEventTitle(event.title);
      setEstimatedAttendees(event.attendee_estimate || 0);
      setEventOrganizer(event.organizer || "Event Organizer Inc.");
      setEventType(""); // Assuming event.type does not exist in EventFormData
      setEventCategory(""); // Assuming event.category does not exist in EventFormData
      setEventSearchTag(event.tags || []);
      const eventTags = event.tags || [];
      //setSelectedTags(eventTags);
      setSelectedTags(tagOptions.filter(tag => eventTags.includes(tag.value)));
      setSearchVenueQuery(event.venue || "");
      setActiveEventLocation(event.locationType || "");
      setIsRecurring(event.isRecurring || false);
      setIsMultiVenue(event.isMultiVenue || false);
      setEventStartDate(formatToDateString(new Date(event.startDate.toString())));
      setEventStartTime(event.startTime || formatToTimeString(new Date()));
      setEventEndDate(formatToDateString(new Date(event.endDate.toString())));
      setEventEndTime(event.endTime || formatToTimeString(new Date()));
      setShowStartTime(event.showStartTime);
      setShowEndTime(event.showEndTime);
      setEventTimeZone(event.timeZone || "UTC");
      setEventPageLanguage(event.language || "English");
      //setImage(event.image);
      setEventSummary(event.summary);
      setContent(event.description || "");
      setEventStatus(event.status || "Draft");
      setFormIsPopulated(true);
      setUserSdgId(event.userSdgId || "");
    }
  }, [event]);

  useEffect(() => {
    fetchUserSdgs();
  }, [userId]);

  useEffect(() =>{
    if(userSdgId && userSdgList){
      setSelectedUserSdg(userSdgList.find(usdg => usdg._id === userSdgId));  
    }
  }, [userSdgId, userSdgList])

  const fetchUserSdgs = async () => {
    if (userId) {
      try {
        const response = await userSdgAPI.getSdgsByUser(userId);
        setUserSdgList(response.userSdgs);
      } catch (error) {
        console.error("Error fetching user SDGs:", error);
      }
    }
  };
    
  const handleStartCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setShowStartTime(checked);
    if (!checked) {
      setEventStartTime("");
    }
  };
  const handleEndCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setShowEndTime(e.target.checked);
    const checked = e.target.checked;
    setShowEndTime(e.target.checked);
    if (!checked) {
      setEventEndTime("");
    }
  };

  const handleTimeZoneSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setEventTimeZone(event.target.value);
    // console.log(event.target.value);
  };
  const handlePageLanguageSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setEventPageLanguage(event.target.value);
    // console.log(event.target.value);
  };

  const handleEventSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setEventOrganizer(event.target.value);
    // console.log(event.target.value);
  };
  const handleTypeSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setEventType(event.target.value);
    // console.log(event.target.value);
  };
  const handleCategorySelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setEventCategory(event.target.value);
    // console.log(event.target.value);
  };

  const handleTagChange = (selectedOptions: any) => {
    const arr = selectedOptions.map((item: any) => item.value);
    setSelectedTags(selectedOptions);
    setEventSearchTag(arr);
  };

  // form action

  function resetFrom() {
    setEventTitle("");
    setEventOrganizer("");
    setEventType("");
    setEventCategory("");
    setEventSearchTag([]);
    setSearchVenueQuery("");
    setSearchVenueQuery("");
    setActiveEventLocation("");
    setIsRecurring(null);
    setEventStartDate(formatToDateString(new Date()));
    setEventStartTime(formatToDateString(new Date()));
    setEventEndDate(formatToDateString(new Date()));
    setEventEndTime(formatToDateString(new Date()));
    setEventTimeZone("");
    setEventPageLanguage("");
    setImage("");
    setEventSummary("");
    setContent("");
    setUserSdgId("");
  }

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    if(!userId) throw Error("User currently not logged in");

    const eventData: EventFormData = {
      userId: userId,
      title: eventTitle,
      organizer: eventOrganizer,
      tags: eventSearchTag,
      locationType: activeEventLocation,
      venue: searchVenueQuery,
      isRecurring: isRecurring,
      startDate: convertToISOString(eventStartDate),
      startTime: eventStartTime,
      endDate: convertToISOString(eventEndDate),
      endTime: eventEndTime,
      showStartTime: showStartTime,
      showEndTime: showEndTime,
      timeZone: eventTimeZone,
      language: eventPageLanguage,
      // image: image,
      summary: eventSummary,
      description: content,
      attendee_estimate: estimatedAttendees,
      supplier_estimate: supplier_estimate,
      status: eventStatus,
      userSdgId: userSdgId,
      tenantOwnerEmail: userEmail
    };
    // console.log({ eventData });

    setIsLoading(true);
    try {
      const res = id? await eventsAPI.updateEvent(eventData, id) : await eventsAPI.createEvent(eventData);
      if (res) {
        Swal.fire({
          position: "center",
          icon: "success",
          title: `Event ${id? "Updated": "Created"} Successfully!`,
          showConfirmButton: false,
          timer: 1500,
        }).then(() => {
          setIsLoading(false);
          resetFrom();
          navigate(pageNames.EVENT);
        });
      }
    } catch (error:any) {

      const apiError = error;

      if(apiError?.code === "EVENT_LIMIT_REACHED"){
        Swal.fire({
          icon: "error",
          title: "Event limit reached",
          html: `
            <p>${apiError?.message}</p>
            <small>
              Plan: <strong>${apiError?.meta?.planId}</strong><br/>
              Events used: ${apiError?.meta?.currentCount} / ${apiError?.meta?.maxEvents}
            </small>
          `,
          confirmButtonText: "Upgrade plan",
          showCancelButton: true,
          cancelButtonText: "Close",
        }).then((result) => {
          setIsLoading(false);
          if (result.isConfirmed) {
            navigate("/billing"); // or wherever your upgrade flow lives
          }
        });
      }
      else{
        Swal.fire({
          icon: "error",
          title: "Something went wrong",
          html: `<p>it is possible one of the required fields is missing</p>`
        }).then((result) => {
          setIsLoading(false);
        });
      }  
    }
  };
  return (
    <div className='container'>
      <form onSubmit={handleSubmit}>
        {/* Materiality/SDG Association Info */}
        <FormSectionContainer
          isHr={true}
          className='sdg-priority'
          icon={faLeaf}
          title='SDG Goal Priority'
          description='Select which Sustainable Development Goals are prioritized for this event.'
        >
          <SubscriptionGate email={userEmail}>
            <div className='mt-2'>
                <UserSdgSelect 
                  userSdgList={userSdgList} 
                  selectedUserSdg={selectedUserSdg} 
                  setUserSdgId={setUserSdgId} />
            </div>
          </SubscriptionGate>
        </FormSectionContainer>    

        {/* Basic Info */}
        <FormSectionContainer
          isHr={true}
          className='basic-info'
          icon={faTextSlash}
          title='Basic Info'
          description='Name your event title. Add details to help the platform recommend accurate sustainable actions'
        >
          <div>
            {/* basic info field */}
            <div className='form-floating mb-3'>
              <input
                maxLength={evenTitleLimit}
                type='text'
                className='form-control'
                id='evenTitle'
                placeholder='name@example.com'
                required
                value={eventTitle}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEventTitle(e.target.value)}
              />
              <label htmlFor='evenTitle'>
                Event Title <span className='text-danger'>*</span>
              </label>
              {eventTitle.length == evenTitleLimit && (
                <p className='text-danger fs-12 mb-0'>You have typed over limit</p>
              )}
              <p className='text-muted text-end fs-12 mb-0'>
                {eventTitle.length}/{evenTitleLimit}
              </p>
            </div>
            {/* Hidden for now  */}
            <div className='form-floating mb-3 d-none'>
              <select
                className='form-select'
                id='organizer'
                aria-label='Floating label select example'
                value={eventOrganizer}
                onChange={handleEventSelectChange}
              >
                <option selected value='Event Organizer Inc.'>
                  Event Organizer Inc.
                </option>
                <option value='Event Organizer Inc.2'>Event Organizer Inc2.</option>
                <option value='Event Organizer Inc.3'>Event Organizer Inc.3</option>
              </select>
              <label htmlFor='organizer'>Organizer</label>
              <p className='text-muted fs-12 mb-0'>
                The profile show a unique organizer and shows all events on one page{" "}
                <Link to='/'>View Organizer Info</Link>
              </p>
            </div>
            {/* Hidden for now  */}    
            <div className='row g-2 mb-3 d-none'>
              <div className='col-12 col-md-4'>
                <div className='form-floating'>
                  <select
                    className='form-select'
                    id='type'
                    aria-label='Floating label select example'
                    value={eventType}
                    onChange={handleTypeSelectChange}
                  >
                    <option value='0' selected>
                      Type
                    </option>
                    <option value='1'>One</option>
                    <option value='2'>Two</option>
                    <option value='3'>Three</option>
                  </select>
                  <label htmlFor='type'>Works with selects</label>
                </div>
              </div>
              <div className='col-12 col-md-4'>
                <div className='form-floating'>
                  <select
                    className='form-select'
                    id='Category'
                    aria-label='Floating label select example'
                    value={eventCategory}
                    onChange={handleCategorySelectChange}
                  >
                    <option value='0' selected>
                      Category
                    </option>
                    <option value='1'>One</option>
                    <option value='2'>Two</option>
                    <option value='3'>Three</option>
                  </select>
                  <label htmlFor='floatingSelectGrid'>Category</label>
                </div>
              </div>
            </div>

            <div>
              <h4 className='mt-5'>Tags</h4>
              <p className=''>
                By adding relevant tags to your event, our action recommendation engine understands your event purpose even more
              </p>

              {/* <div className='form-floating mb-3'>
                <input
                  maxLength={25}
                  type='text'
                  className='form-control'
                  id='evenTitle'
                  placeholder='search tag'
                  value={eventSearchTag}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEventSearchTag(e.target.value)}
                />

                <label htmlFor='evenTitle'>Please enter to add a tag</label>
                <div className='d-flex justify-content-between align-items-center'>
                  <p className='text-muted fs-12 mb-0'>0/10 tags</p>
                  <p className='text-muted fs-12 mb-0'>{eventSearchTag.length}/25</p>
                </div>
              </div> */}

              <div className='mb-3'>
                <Select
                  placeholder='Please enter to add a tag'
                  onChange={handleTagChange}
                  options={tagOptions}
                  isMulti
                  isSearchable
                  value={selectedTags}
                  
                />
                <div className='d-flex justify-content-between align-items-center'>
                  <p className='text-muted fs-12 mb-0'>{selectedTags.length}/{tagOptions.length} tags</p>
                  <p className='text-muted fs-12 mb-0'>{eventSearchTag.length}/{tagOptions.length}</p>
                </div>
              </div>
            </div>
          </div>

          {/* basic info field - Estimated Attendees */}
          <div>
              <h4 className='mt-5'>Attendee Estimate</h4>
              <p className=''>
                Adding this information will make it possible, in the future, to understand your event impact on a per attendee basis 
              </p>
              <div className='form-floating mb-3'>
              <input
                type='number'
                className='form-control'
                id='estAttendees'
                placeholder='e.g 150'
                value={estimatedAttendees}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEstimatedAttendees(parseFloat(e.target.value))}
              />
              <label htmlFor='evenTitle'>
                Estimated Number Of Attendees <span className='text-danger'>*</span>
              </label>
          </div>
          </div>

        </FormSectionContainer>

        {/* location */}

        <FormSectionContainer
          isHr={true}
          className='location'
          icon={faMap}
          title='Location (s)'
          description='Help our sustainability action recommendation engine by selecting wether your event is physical or online'
        >
          <div>
            <div className='d-flex flex-wrap'>
              <button
                type='button'
                className={`my-1 me-3 btn btn-light ${
                  activeEventLocation === "Physical" ? "btn-select-active" : "btn-select"
                }`}
                onClick={() => setActiveEventLocation("Physical")}
              >
                Physical Venue
              </button>
              <button
                type='button'
                className={`my-1 me-3 btn btn-light ${
                  activeEventLocation === "Online" ? "btn-select-active" : "btn-select"
                }`}
                onClick={() => setActiveEventLocation("Online")}
              >
                Online Event
              </button>
              <button
                type='button'
                className={`my-1 me-3 btn btn-light ${
                  activeEventLocation === "To-be-announced" ? "btn-select-active" : "btn-select"
                }`}
                onClick={() => setActiveEventLocation("To-be-announced")}
              >
                To be announced
              </button>
            </div>
            <p className='py-3'>Select wether this is a Multi venue or Single venue event. Online events are single venue unless they use multiple, different online platforms.</p>
            <div className='d-flex flex-wrap'>
              <button
                type='button'
                className={`my-1 me-3 btn btn-light ${
                  isMultiVenue === false ? "btn-select-active" : "btn-select"
                }`}
                onClick={() => setIsMultiVenue(false)}
              >
                Single Venue
              </button>
              <button
                type='button'
                className={`my-1 me-3 btn btn-light ${
                  isMultiVenue === true ? "btn-select-active" : "btn-select"
                }`}
                onClick={() => setIsMultiVenue(true)}
              >
                Multiple Venues
              </button>
            </div>
            
            {/* search */}
            {!isMultiVenue && 
              <div>
                <h6 className='pt-3'>Venue Location</h6>
                <div className='position-relative my-2 me-0 me-md-3'>
                  {!searchVenueQuery && (
                    <span className='position-absolute top-50 translate-middle' style={{ left: "20px" }}>
                      <SearchIcon />
                    </span>
                  )}
                  <input
                    value={searchVenueQuery}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchVenueQuery(e.target.value)}
                    type='text'
                    className='form-control search-field'
                    id='search'
                    placeholder='Search for venue or a address'
                  />
                </div>
              </div>  
            }
          </div>
        </FormSectionContainer>

        {/* time and date */}

        <FormSectionContainer
          isHr={true}
          className='time-date'
          icon={faCalendarDays}
          title='Time And Date'
          description='Adding this information will make it possible, in the future, to find time and date specific ideas/solutions for your event'
        >
          <div>
            <div className='d-flex flex-wrap'>
              <button
                type='button'
                className={`my-1 me-3 btn btn-light ${
                  isRecurring === false ? "btn-select-active" : "btn-select"
                }`}
                onClick={() => setIsRecurring(false)}
              >
                Single Event
              </button>
              <button
                type='button'
                className={`my-1 me-3 btn btn-light ${
                  isRecurring === true ? "btn-select-active" : "btn-select"
                }`}
                onClick={() => setIsRecurring(true)}
              >
                Recurring Event
              </button>
            </div>
            <p className='py-3'>Single event happens once and can last multiple days</p>
            {/* start date time */}
            <div className='row g-2 my-3'>
              <div className='col-md-6'>
                <div className='form-floating'>
                  <input
                    type='date'
                    className='form-control'
                    id='floatingInputGrid'
                    required
                    value={eventStartDate}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setEventStartDate(formatToDateString(new Date(e.target.value),'...'))
                    }
                  />
                  <label htmlFor='floatingInputGrid'>
                    Event starts <span className='text-danger'>*</span>
                  </label>
                </div>
              </div>
              {showStartTime && (
                <div className='col-md-6'>
                  <div className='form-floating'>
                    <input
                      type='time'
                      className='form-control'
                      id='floatingInputGrid'
                      value={eventStartTime}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setEventStartTime(
                          formatToTimeString(new Date(`${formatToDateString(new Date())}T${e.target.value}`))
                        )
                      }
                    />
                    <label htmlFor='floatingInputGrid'>Start Time</label>
                  </div>
                </div>
              )}
            </div>
            {/* end date time */}
            <div className='row g-2 my-3'>
              <div className='col-md-6'>
                <div className='form-floating'>
                  <input
                    type='date'
                    className='form-control'
                    id='floatingInputGrid'
                    required
                    value={eventEndDate}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setEventEndDate(formatToDateString(new Date(e.target.value)))
                    }
                  />
                  <label htmlFor='floatingInputGrid'>
                    Event ends <span className='text-danger'>*</span>
                  </label>
                </div>
              </div>
              {showEndTime && (
                <div className='col-md-6'>
                  <div className='form-floating'>
                    <input
                      type='time'
                      className='form-control'
                      id='floatingInputGrid'
                      value={eventEndTime}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setEventEndTime(
                          formatToTimeString(new Date(`${formatToDateString(new Date())}T${e.target.value}`))
                        )
                      }
                    />
                    <label htmlFor='floatingInputGrid'>End Time</label>
                  </div>
                </div>
              )}
            </div>
            {/* Hidden for now */}
            <div className='my-3 d-none'>
              <div className='form-check my-2'>
                <input
                  className='form-check-input'
                  type='checkbox'
                  value=''
                  id='flexCheckDefault'
                  checked={showStartTime}
                  onChange={handleStartCheckboxChange}
                />
                <label className='form-check-label ms-2' htmlFor='flexCheckDefault'>
                  Display start time
                  <span className='d-block fs-12'>
                    The start time of your event will be displayed to attendees
                  </span>
                </label>
              </div>
              <div className='form-check my-2'>
                <input
                  className='form-check-input'
                  type='checkbox'
                  value=''
                  id='flexCheckDefault'
                  checked={showEndTime}
                  onChange={handleEndCheckboxChange}
                />
                <label className='form-check-label ms-2' htmlFor='flexCheckDefault'>
                  Display end time
                  <span className='d-block fs-12'>
                    The end time of your event will be displayed to attendees
                  </span>
                </label>
              </div>
            </div>

            {/* time zone */}
            <div className='row'>
              <div className='col-md-6'>
                  <TimeZoneSelect 
                    onChange={handleTimeZoneSelectChange} 
                    value={eventTimeZone} 
                    className="form-floating mb-3"/>
              </div>
            </div>

            {/* Hidden for now */}
            <div className='row d-none'>
              <div className='col-md-6'>
                <div className='form-floating mb-3'>
                  <select
                    className='form-select'
                    id='PageLanguage'
                    aria-label='Floating label select example'
                    value={eventPageLanguage}
                    onChange={handlePageLanguageSelectChange}
                  >
                    <option selected value='English'>
                      English
                    </option>
                    <option value='Spanish'>Spanish</option>
                    <option value='French'>French</option>
                    <option value='German'>German</option>
                    <option value='Russian'>Russian</option>
                    <option value='Japanese'>Japanese</option>
                  </select>
                  <label htmlFor='PageLanguage'>Page Language Language</label>
                </div>
              </div>
            </div>
          </div>
        </FormSectionContainer>

        {/* event media */}

        <div className='d-none'>              
          <FormSectionContainer
            isHr={true}
            className='event-media'
            icon={faImage}
            title='Event Media'
            subTitle='Images'
            description='Add photo to show to what your event will be about. You can upload up to 10 images.'
          >
            <div>
              {displayMediaAlert && <ImageAlert setDisplayMediaAlert={setDisplayMediaAlert} />}
              <ImageUploader image={image} setImage={setImage} />
            </div>
          </FormSectionContainer>
        </div>               
        {/* Summary */}

        <FormSectionContainer className='summary' icon={faTengeSign} title='Summary' description=''>
          <div>
            <p>
              This short summary will be used to help us suggest credible sustainability actions for your event and will also be on your event report. (140 characters max){" "}
              <Link to='/example' className='text-decoration-none d-none'>
                See example
              </Link>
            </p>

            <div className='form-floating mb-3'>
              <textarea
                maxLength={evenSummaryLimit}
                className='form-control'
                id='evenSummary'
                placeholder='summary'
                required
                value={eventSummary}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setEventSummary(e.target.value)}
              />
              <label htmlFor='evenSummary'>
                Summary <span className='text-danger'>*</span>
              </label>
              {eventSummary.length == evenSummaryLimit && (
                <p className='text-danger fs-12 mb-0'>You have typed over limit</p>
              )}
              <p className='text-muted text-end fs-12 mb-0'>
                {eventSummary.length}/{evenSummaryLimit}
              </p>
            </div>

            <Link to='/suggest-summary' className='text-decoration-none fw-bolder d-none'>
              <FontAwesomeIcon icon={faBolt} /> Suggest summary
            </Link>
          </div>
        </FormSectionContainer>

        {/* Description */}

        <FormSectionContainer
          className='description pt-5'
          icon={faTextWidth}
          title='Description'
          description=''
        >
          <div className="mb-3">
            <p>
              Add any additional information about your event eg schedule, sponsors. Including this information will help tailor specific solutions for your event.
              <Link to='/learn-details' className='text-decoration-none d-none'>
                learn more
              </Link>
            </p>

            {/* <div className='form-floating mb-3'>
              <textarea
                className='form-control'
                id='evenDescription'
                placeholder='Description'
                required
                value={eventDescription}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setEventDescription(e.target.value)}
              />
              <label htmlFor='evenDescription'>
                Description <span className='text-danger'>*</span>
              </label>
            </div> */}

            <Description content={content} setContent={setContent} />
            {/* <TTT /> */}

            <Link to='/suggest-description' className='text-decoration-none fw-bolder my-5 d-inline-block d-none  '>
              <FontAwesomeIcon icon={faBolt} /> Suggest Description
            </Link>
          </div>
        </FormSectionContainer>
                    
        <div className='text-end pb-4 my-4'>
          <button type='submit' className='btn btn-primary py-3 px-4'>
            {id? `Update Event`: `Create Event`} {isLoading && <LoaderSm />}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EventForm;
